import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, useQuery, queryOptions } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SiteLayout } from "@/components/site/Layout";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { getPostBySlug, listRelatedPosts } from "@/lib/blog.functions";
import { BRAND, absoluteUrl } from "@/lib/brand";
import { getLeadMagnet } from "@/lib/lead-magnets";
import { LeadMagnetBlock } from "@/components/site/LeadMagnetBlock";

const postQuery = (slug: string) =>
  queryOptions({
    queryKey: ["post", slug],
    queryFn: async () => {
      const p = await getPostBySlug({ data: { slug } });
      if (!p) throw notFound();
      return p;
    },
  });

const relatedQuery = (slug: string, tags: string[]) =>
  queryOptions({
    queryKey: ["post-related", slug, tags],
    queryFn: () => listRelatedPosts({ data: { slug, tags, limit: 3 } }),
  });

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params, context }) => context.queryClient.ensureQueryData(postQuery(params.slug)),
  component: PostPage,
  head: ({ params, loaderData }) => {
    const p: any = loaderData;
    const url = `https://joinkcn.com/blog/${params.slug}`;
    const title = p?.seo_title || p?.title || "Post - KCN";
    const description = p?.seo_description || p?.excerpt || "";
    const image = p?.cover_image_url;
    const faqs: Array<{ q: string; a: string }> = Array.isArray(p?.faqs) ? p.faqs : [];
    const scripts: any[] = p
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: p.title,
              description,
              datePublished: p.published_at,
              dateModified: p.updated_at,
              author: { "@type": "Organization", name: p.author_name || BRAND.name },
              publisher: { "@type": "Organization", name: BRAND.name, logo: { "@type": "ImageObject", url: absoluteUrl(BRAND.logos.onWhite) } },
              image: image ? [image] : undefined,
              mainEntityOfPage: url,
              keywords: (p.tags ?? []).join(", "),
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://joinkcn.com/" },
                { "@type": "ListItem", position: 2, name: "MOTM Insights", item: "https://joinkcn.com/blog" },
                { "@type": "ListItem", position: 3, name: p.title, item: url },
              ],
            }),
          },
        ]
      : [];
    if (faqs.length) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      });
    }
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        ...(image ? [{ property: "og:image", content: image }, { name: "twitter:image", content: image }] : []),
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  errorComponent: ({ error }) => <SiteLayout><Section><p className="text-primary">{error instanceof Error ? error.message : "This post didn't load."}</p></Section></SiteLayout>,
  notFoundComponent: () => (
    <SiteLayout>
      <Section>
        <h1 className="headline-lg">Post not found</h1>
        <Link to="/blog" className="mt-6 inline-block text-primary underline">Back to blog</Link>
      </Section>
    </SiteLayout>
  ),
});

function PostPage() {
  const { slug } = Route.useParams();
  const { data: p } = useSuspenseQuery(postQuery(slug));
  const takeaways: string[] = Array.isArray((p as any).key_takeaways) ? (p as any).key_takeaways : [];
  const faqs: Array<{ q: string; a: string }> = Array.isArray((p as any).faqs) ? (p as any).faqs : [];
  const { data: related = [] } = useQuery(relatedQuery(slug, p.tags ?? []));
  const magnet = getLeadMagnet((p as any).lead_magnet_key);

  return (
    <SiteLayout>
      <article>
        <Section tone="dark" className="pt-36 md:pt-36 lg:pt-36 pb-12">
          <div className="max-w-3xl">
            <SectionEyebrow>{new Date(p.published_at!).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</SectionEyebrow>
            <h1 className="font-display font-light tracking-tight leading-[1.05] text-3xl md:text-4xl lg:text-5xl mb-6">{p.title}</h1>
            {p.excerpt ? <p className="text-xl text-foreground/80 leading-relaxed">{p.excerpt}</p> : null}
            <div className="mt-6 text-sm text-foreground/55">By {p.author_name}</div>
            {magnet ? (
              <a
                href="#lead-magnet"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("lead-magnet")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="mt-8 inline-flex items-center gap-3 bg-primary px-6 py-4 text-xs uppercase tracking-[0.2em] text-primary-foreground hover:bg-primary/90"
              >
                Get the free {magnet.eyebrow.replace(/^Free\s+/i, "").toLowerCase()} <span aria-hidden>↓</span>
              </a>
            ) : null}
          </div>
        </Section>

        {p.cover_image_url ? (
          <div className="container-edge -mt-4 mb-4">
            <img src={p.cover_image_url} alt="" className="w-full aspect-[16/9] object-cover" />
          </div>
        ) : null}

        <Section tone="cream" className="pt-8">
          <div className="max-w-3xl mx-auto">
            {takeaways.length ? (
              <aside
                aria-label="Key takeaways"
                className="mb-10 border border-cream-foreground/20 bg-cream-foreground/[0.04] p-6"
              >
                <div className="text-xs uppercase tracking-[0.2em] text-cream-foreground/65 mb-3">Key takeaways</div>
                <ul className="space-y-2 list-disc pl-5 text-cream-foreground/90">
                  {takeaways.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </aside>
            ) : null}

            <div className="prose-blog prose-on-cream">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.body_markdown}</ReactMarkdown>
            </div>

            {magnet ? (
              <LeadMagnetBlock magnet={magnet} />
            ) : (
            /* Inline Rent vs. Own CTA */
            <aside className="mt-12 border border-cream-foreground/20 p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-cream-foreground/65 mb-1">Stop renting attention</div>
                <p className="font-display text-2xl md:text-3xl text-cream-foreground leading-tight">
                  See how buyers, sellers, and AI find you.
                </p>
              </div>
              <Link
                to="/visibility-audit"
                className="inline-block bg-primary px-6 py-3 text-xs uppercase tracking-[0.2em] text-primary-foreground hover:bg-primary/90 whitespace-nowrap"
              >
                Get your free visibility audit
              </Link>
            </aside>
            )}

            {faqs.length ? (
              <section className="mt-14" aria-labelledby="faq-heading">
                <div className="text-xs uppercase tracking-[0.2em] text-cream-foreground/65 mb-3">Frequently asked</div>
                <div id="faq-heading" className="font-display text-xl md:text-2xl text-cream-foreground mb-6">Questions readers ask</div>
                <div className="divide-y divide-cream-foreground/20 border-y border-cream-foreground/20">
                  {faqs.map((f, i) => (
                    <details key={i} className="group py-4">
                      <summary className="cursor-pointer list-none flex justify-between items-start gap-4 text-cream-foreground font-medium">
                        <span>{f.q}</span>
                        <span className="text-primary text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                      </summary>
                      <div className="mt-3 text-cream-foreground/80 leading-relaxed whitespace-pre-line">{f.a}</div>
                    </details>
                  ))}
                </div>
              </section>
            ) : null}

            <div className="mt-16 pt-10 border-t border-cream-foreground/20">
              <Link to="/blog" className="text-xs uppercase tracking-[0.18em] text-cream-foreground/65 hover:text-cream-foreground">← Back to blog</Link>
            </div>
          </div>
        </Section>

        {related.length ? (
          <Section tone="dark" className="py-20">
            <SectionEyebrow>Keep reading</SectionEyebrow>
            <div className="font-display font-light tracking-tight text-2xl md:text-3xl mb-10">Related MOTM insights</div>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/blog/$slug"
                  params={{ slug: r.slug }}
                  className="group block border border-foreground/15 hover:border-primary transition-colors"
                >
                  {r.cover_image_url ? (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={r.cover_image_url} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    </div>
                  ) : null}
                  <div className="p-5">
                    <div className="text-xs uppercase tracking-[0.18em] text-foreground/55 mb-2">
                      {r.published_at ? new Date(r.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null}
                    </div>
                    <div className="font-display text-xl text-foreground group-hover:text-primary leading-snug">{r.title}</div>
                    {r.excerpt ? <p className="mt-2 text-sm text-foreground/70 line-clamp-3">{r.excerpt}</p> : null}
                  </div>
                </Link>
              ))}
            </div>
          </Section>
        ) : null}

      </article>
    </SiteLayout>
  );
}
